import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GsLayout, GsHeader, GsSidebar, useDarkMode, GsErrorBoundary, GsErrorPage, GsVersionBanner, BrandingProvider, GsLoginScreen } from '@gs/ui';
import type { SidebarItem } from '@gs/ui';
import { getConfig } from '@gs/config/browser';
import {
  LayoutDashboard,
  LayoutGrid,
  BarChart2,
  BookOpen,
  Settings2,
} from 'lucide-react';
import { useAuth, GsRole, hasRole } from '@gs/auth';

import MobileBottomNav from './components/MobileBottomNav';
import { PwaUpdateBanner } from './components/PwaUpdateBanner';
import { ALL_APPS } from './data/apps';
import { registerApiAuth, clearApiAuth } from './services/api';
import DashboardPage from './pages/DashboardPage';
import AppsPage from './pages/AppsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import UsersPage from './pages/UsersPage';

const navItems: SidebarItem[] = [
  { key: 'dash', label: 'Mon tableau de bord', icon: LayoutDashboard, path: '/' },
  { key: 'apps', label: 'Toutes les apps', icon: LayoutGrid, path: '/apps', badge: ALL_APPS.length },
  { key: 'analytics', label: 'Analytique', icon: BarChart2, path: '/analytique' },
  { key: 'docs', label: 'Documentation', icon: BookOpen, path: '/docs' },
];

const adminItems: SidebarItem[] = [
  {
    key: 'gs-admin',
    label: 'GS-Admin',
    icon: Settings2,
    path: 'https://gs-admin-d4hvgqhsgud4e3b0.canadacentral-01.azurewebsites.net',
    externalUrl: true,
  },
];

function hubPageTitle(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'Tableau de bord';
  if (pathname.startsWith('/apps')) return 'Applications';
  if (pathname.startsWith('/analytique')) return 'Analytique';
  if (pathname.startsWith('/docs')) return 'Documentation';
  if (pathname.startsWith('/utilisateurs')) return 'Utilisateurs';
  return 'GS-Hub';
}

function gsRoleLabel(role: GsRole): string {
  switch (role) {
    case GsRole.ADMIN:
      return 'Administrateur';
    case GsRole.GESTIONNAIRE:
      return 'Gestionnaire';
    default:
      return 'Utilisateur';
  }
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout, getAccessToken } = useAuth();

  if (user) {
    registerApiAuth(getAccessToken);
  } else {
    clearApiAuth();
  }

  useEffect(() => {
    return () => {
      clearApiAuth();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <GsLoginScreen
        appName="Groupe Signalisation"
        logoSrc="/logo-gs.png"
        logoSrcDark="/logo-gs-white.png"
        onLogin={() => void login()}
      />
    );
  }

  return (
    // Theming runtime ADR-059 (rollout post-pilote ug) : provider CLIENT seulement.
    // Le serveur hub n'a ni requireAuth ni @gs/db (deliberement, voir server/index.ts) :
    // /api/branding tombe sur le 404 /api/* existant -> fail-soft = fallback :root
    // GS 2026 (no-op visuel). Montage serveur a faire quand hub aura auth + SQL.
    <BrandingProvider enabled={!!user}>
    <GsLayout
      header={<GsHeader pageTitle={hubPageTitle(location.pathname)} />}
      sidebar={
        <GsSidebar
          variant="light"
          headerLogoSrc="/logo-gs.png"
          headerLogoSrcDark="/logo-gs-white.png"
          appName="GS-Hub"
          mainSectionLabel="Navigation"
          adminSectionLabel="Admin"
          items={navItems}
          adminItems={adminItems}
          isAdmin={hasRole(user, GsRole.ADMIN)}
          activePath={location.pathname}
          onNavigate={(path: string) => navigate(path)}
          bottomUser={{
            name: user.name,
            email: user.email,
            role: gsRoleLabel(user.role),
            initials: user.initials,
          }}
          isDark={isDark}
          onToggleDark={toggleDark}
          onLogout={() => void logout()}
          appVersion="0.1.0"
        />
      }
      mobileNav={<MobileBottomNav />}
    >
      <GsVersionBanner apiBaseUrl={getConfig().apiUrl} />
      <PwaUpdateBanner />
      <GsErrorBoundary>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/analytique" element={<PlaceholderPage title="Analytique" />} />
          <Route path="/docs" element={<PlaceholderPage title="Documentation" />} />
          <Route path="/utilisateurs" element={<UsersPage />} />
          <Route path="/error/:code" element={<GsErrorPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </GsErrorBoundary>
    </GsLayout>
    </BrandingProvider>
  );
}
